import React from "react";
import { motion } from "framer-motion";
import BabasahebPortrait from "../components/BabasahebPortrait";

const TIMELINE = [
  { year: "1891", title: "Birth of Babasaheb", desc: "Born into adversity, rose through scholarship and conviction." },
  { year: "1923", title: "D.Sc. from London", desc: "Completed doctorate at the LSE; a lifelong UK connection begins." },
  { year: "1949", title: "Constitution of India", desc: "Chaired the Drafting Committee; the world's longest written constitution." },
  { year: "1956", title: "Return to the Buddha", desc: "Embraced Buddhism with 500,000+ followers; a historic awakening." },
  { year: "2026", title: "ABGN-UK Founded", desc: "UK-wide Ambedkarite Buddhist community, digitally united." },
];

export default function About() {
  return (
    <>
      <section className="bg-navy text-pearl py-24" data-testid="about-hero">
        <div className="section-container">
          <p className="eyebrow text-gold">About Us</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-tight max-w-4xl">
            Who we are, and why we gather.
          </h1>
          <p className="mt-6 max-w-2xl text-pearl/80 text-lg leading-relaxed">
            ABGN-UK is a cloud-native, member-governed community carrying forward the legacy of
            Dr. B.R. Ambedkar and the Buddha's Dhamma across the United Kingdom.
          </p>
        </div>
      </section>

      <section className="py-24 bg-pearl">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <BabasahebPortrait />
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-slate-500">
              Babasaheb Dr. B.R. Ambedkar · 1891 — 1956
            </p>
            <p className="mt-2 text-saffron text-sm">Father of the Indian Constitution</p>
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow">Our mission</p>
            <h2 className="mt-3 font-display text-4xl text-navy">To educate, organise and uplift, together.</h2>
            <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
              <p>
                The Ambedkarite Buddhist Global Network UK exists to foster a just, peaceful and enlightened society
                through study, practice and fellowship. We welcome people of every background who share
                our values of equality, compassion and critical inquiry.
              </p>
              <p>
                Our operations are fully cloud-based: membership, events, donations and governance all
                happen in an accessible, transparent digital environment, enabling members from Edinburgh to
                Southampton to participate on equal footing.
              </p>
              <p>
                To deliver our charitable mission online, ABGN-UK has built its own
                <strong className="text-navy"> digital platform</strong> &mdash; not as a commercial
                product, but as charitable infrastructure. The platform hosts live meditation,
                Dhamma teachings, community meetings, structured educational courses, youth
                programmes, and study circles. It exists so that distance, disability,
                caring responsibilities or the absence of a local Buddhist centre never bar
                anyone from public participation in the Sangha.
              </p>
              <p>
                Inspired by the Three Jewels (Buddha, Dhamma, Sangha) and Babasaheb's Panchsheel of civic
                virtue, we cultivate a community where nobody is left behind.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="section-container">
          <p className="eyebrow">A short timeline</p>
          <h2 className="mt-3 font-display text-4xl text-navy mb-14">From conscience to community.</h2>
          <div className="space-y-8">
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.year}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="grid grid-cols-12 gap-6 items-start border-b border-navy/10 pb-8"
                data-testid={`timeline-${t.year}`}
              >
                <div className="col-span-3 sm:col-span-2 font-display text-3xl text-gold">{t.year}</div>
                <div className="col-span-9 sm:col-span-10">
                  <h3 className="font-display text-2xl text-navy">{t.title}</h3>
                  <p className="mt-1 text-slate-600">{t.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-pearl">
        <div className="section-container grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Vision", body: "An enlightened UK community where every person lives in dignity, freedom and harmony." },
            { title: "Mission", body: "Educate through Dhamma study, organise community programmes, and serve those in need." },
            { title: "Values", body: "Equality, compassion, knowledge, integrity, and the courage to speak truth." },
          ].map((b) => (
            <div key={b.title} className="bg-white p-10 border border-navy/10 rounded-sm">
              <p className="eyebrow">{b.title}</p>
              <p className="mt-4 text-slate-700 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
